'use client';

import { FormEvent, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  User, Building2, Mail, Phone, MessageSquare,
  ChevronDown, Send, CheckCircle, MapPin, Globe,
  Copy, Check
} from 'lucide-react';
import PhoneInput from '@/components/forms/PhoneInput';
import { FloatingInput } from '@/components/forms/FloatingInput';
import { FloatingSelect } from '@/components/forms/FloatingSelect';
import { FloatingTextarea } from '@/components/forms/FloatingTextarea';
import { ConsentSection } from '@/components/forms/ConsentSection';
import { validatePhone } from '@/utils/phoneValidation';
import { phoneCountryToIso } from '@/data/countryCodes';

const spinKeyframes = `
@keyframes cmd-spin {
  to { transform: rotate(360deg); }
}
`;

const floatCSS = `
.floating-field input,
.floating-field select,
.floating-field textarea {
  padding-top: 1.25rem;
  padding-bottom: 0.75rem;
}
.floating-field label {
  position: absolute;
  top: 50%;
  left: 0.875rem;
  transform: translateY(-50%);
  transform-origin: left center;
  pointer-events: none;
  transition: top 0.18s ease, transform 0.18s ease, font-size 0.18s ease, color 0.18s ease, background-color 0.18s ease;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  padding: 0 0.25rem;
}
.floating-field textarea ~ label {
  top: 1rem;
  transform: none;
}
.floating-field input:focus ~ label,
.floating-field select:focus ~ label,
.floating-field textarea:focus ~ label,
.floating-field input:not(:placeholder-shown) ~ label,
.floating-field select:not([value=""]) ~ label,
.floating-field textarea:not(:placeholder-shown) ~ label {
  top: 0;
  transform: translateY(-50%);
  font-size: 0.75rem;
  font-weight: 600;
  color: #1e5f9d;
  background: #f8faff;
  padding: 0 0.4rem;
  letter-spacing: 0.01em;
}
.floating-field textarea:focus ~ label,
.floating-field textarea:not(:placeholder-shown) ~ label {
  top: 0;
  transform: translateY(-50%);
}
.floating-field select {
  background-image: none;
}
`;

const ease = [0.25, 0.1, 0.25, 1] as const;

/* ─── original left panel ───────────────────────────────────────────── */

function OriginalLeftPanel() {
  return (
    <div className="relative flex h-full flex-col justify-between overflow-hidden bg-[linear-gradient(135deg,#0f3f9c_0%,#1065b0_48%,#12a3bd_100%)] p-8 [clip-path:polygon(0_0,100%_0,100%_85%,0_100%)]">
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute rounded-full"
          style={{
            width: 400, height: 400, filter: 'blur(60px)', top: -100, right: -100,
            background: 'radial-gradient(circle, rgba(44,120,185,0.18) 0%, transparent 70%)'
          }} />
        <div className="absolute rounded-full"
          style={{
            width: 300, height: 300, filter: 'blur(50px)', bottom: -60, left: -40,
            background: 'radial-gradient(circle, rgba(5,154,143,0.12) 0%, transparent 65%)'
          }} />
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 500 700" fill="none">
          {[...Array(8)].map((_, i) => {
            const a = (i * 45 * Math.PI) / 180;
            return <line key={`r${i}`} x1="250" y1="350"
              x2={250 + 400 * Math.cos(a)} y2={350 + 400 * Math.sin(a)}
              stroke="rgba(44,120,185,0.06)" strokeWidth={0.5} />;
          })}
          {[120, 220, 340].map((r) => (
            <circle key={`c${r}`} cx="250" cy="350" r={r}
              stroke="rgba(44,120,185,0.08)" strokeWidth={0.5}
              strokeDasharray="4 8" fill="none" />
          ))}
        </svg>
        {[
          { x: '15%', y: '20%', r: 2, o: 0.25 },
          { x: '75%', y: '15%', r: 1.5, o: 0.15 },
          { x: '60%', y: '70%', r: 2.5, o: 0.3 },
          { x: '25%', y: '80%', r: 1.8, o: 0.2 },
          { x: '85%', y: '55%', r: 2, o: 0.18 },
          { x: '40%', y: '45%', r: 1.5, o: 0.22 },
        ].map((d, i) => (
          <div key={`d${i}`} className="absolute rounded-full bg-[#2C78B9]"
            style={{ left: d.x, top: d.y, width: d.r, height: d.r, opacity: d.o }} />
        ))}
      </div>

      <div className="relative z-10 flex h-full flex-col justify-between space-y-8">
        {/* Top section */}
        <div>
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-[rgba(44,120,185,0.3)] bg-[rgba(44,120,185,0.1)] px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[#2C78B9] animate-[livePulse_2s_ease-out_infinite]" />
            <span className="text-[10px] font-bold tracking-[0.1em] text-white uppercase">Get in touch</span>
          </motion.div>

          <div className="mt-5">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15, ease }}
              className="leading-[1.1]"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 900, letterSpacing: '-0.04em', color: '#ffffff' }}>
              Let&apos;s build something intelligent.
            </motion.div>
          </div>

          <motion.p custom={0.4} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35, ease }}
            className="mt-3 max-w-[22rem] text-[13px] leading-[1.7] text-white">
            Whether you&apos;re a growing SME or a large enterprise —
            our team is ready to show you what 30 years of ERP expertise looks like in action.
          </motion.p>
        </div>

        {/* Middle — contact info */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5, ease }}
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            {
              icon: <Phone size={14} className="text-[#7ab8e8]" />, label: 'CALL US', value: '+91-9818993537',
              href: 'tel:+919818993537'
            },
            {
              icon: <Mail size={14} className="text-[#7ab8e8]" />, label: 'EMAIL US', value: 'akg@acgil.net',
              href: 'mailto:akg@acgil.net'
            },
            {
              icon: <MapPin size={14} className="text-[#7ab8e8]" />, label: 'VISIT US', value: 'H-107, Sector-63, Noida - 201301, Uttar Pradesh, India',
              href: 'https://maps.google.com/?q=H-107+Sector-63+Noida+201301+Uttar+Pradesh+India'
            },
          ].map((row, i) => (
            <motion.a key={row.label} custom={0.5 + i * 0.08}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 + i * 0.08, ease }}
              href={row.href}
              target={row.href.startsWith('http') ? '_blank' : undefined}
              rel={row.href.startsWith('http') ? 'noreferrer' : undefined}
              className="rounded-xl border border-white/15 bg-black/60 p-4 transition hover:border-[rgba(44,120,185,0.5)] hover:bg-[rgba(44,120,185,0.15)]">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[rgba(44,120,185,0.25)]">
                  {row.icon}
                </span>
                <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-[rgba(255,255,255,0.85)]">{row.label}</div>
              </div>
              <div className="mt-1 text-[13px] font-semibold text-white">{row.value}</div>
            </motion.a>
          ))}
        </motion.div>

        {/* Bottom */}
        <div className="mt-8">

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8, ease }}
            className="mt-3 flex gap-2">
            {[
              {
                href: 'https://www.linkedin.com/company/acgil', label: 'LinkedIn',
                svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              },
              {
                href: 'https://twitter.com/acgil_erp', label: 'Twitter',
                svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              },
            ].map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] transition hover:border-[rgba(44,120,185,0.4)] hover:bg-[rgba(44,120,185,0.2)] hover:text-[#7ab8e8]">
                {s.svg}
              </a>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ─── main component ─────────────────────────────────────────────────── */

interface ContactCommandCenterProps {
  hideLeftPanel?: boolean;
  containerClassName?: string;
  id?: string;
  onSubmit?: (data: Record<string, string>) => Promise<void>;
  onSuccess?: () => void;
}

export default function ContactCommandCenter({ hideLeftPanel = false, containerClassName = '', id, onSubmit, onSuccess }: ContactCommandCenterProps) {
  const reducedMotion = useReducedMotion();
  const dur = reducedMotion ? 0 : 0.5;
  const [copied, setCopied] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    full_name: '', company: '', email: '', phone: '', subject: '', message: ''
  });
  const [phoneCountry, setPhoneCountry] = useState('+91');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const update = (k: string) => (
    e: { target: { value: string } }
  ) => {
    setFormData({ ...formData, [k]: e.target.value });
    if (k === 'phone') {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const countryIso = phoneCountryToIso[phoneCountry] || 'US';
    const phoneErrResult = await validatePhone(formData.phone, countryIso);
    const nextErrors = { phone: phoneErrResult.error ?? undefined };
    setErrors(nextErrors);
    if (phoneErrResult.error) return;

    setStatus('loading');
    try {
      const submission = {
        ...formData,
        phone: phoneCountry ? `${phoneCountry} ${formData.phone}`.trim() : formData.phone,
      };
      if (onSubmit) {
        await onSubmit(submission);
      } else {
        await new Promise((r) => setTimeout(r, 1500));
      }
      setStatus('success');
      if (onSuccess) onSuccess();
      setTimeout(() => {
        setStatus('idle');
        setFormData({ full_name: '', company: '', email: '', phone: '', subject: '', message: '' });
        setErrors({});
      }, 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const copyToClipboard = async (txt: string, k: string) => {
    try { await navigator.clipboard.writeText(txt); setCopied(k); setTimeout(() => setCopied(null), 2000); } catch { }
  };

  const subjectOptions = [
    { value: '', label: 'Select a topic', disabled: true },
    { value: 'mx-erp-mfg', label: 'Mx-ERP — Manufacturing & Trading' },
    { value: 'mx-erp-con', label: 'Mx-ERP — Construction & EPC' },
    { value: 'electra-his', label: 'Electra-HIS — Healthcare Solutions' },
    { value: 'mx-crm', label: 'Mx-CRM — Lead Management' },
    { value: 'general', label: 'General Enquiry' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'support', label: 'Support' },
  ];

  const quickPills = [
    { icon: <Phone size={11} />, text: '+91-120-4355210', key: 'p' },
    { icon: <Mail size={11} />, text: 'akg@acgil.net', key: 'e' },
    { icon: <Globe size={11} />, text: 'www.acgil.com', key: 'w' },
  ];

  return (
    <>
      <style>{spinKeyframes}</style>
      <style>{floatCSS}</style>

      <section id={id} className={`relative ${!hideLeftPanel ? 'mx-auto' : ''} flex max-w-[1440px] flex-col lg:flex-row ${hideLeftPanel ? '' : 'overflow-hidden'} ${containerClassName}`}
        style={{ minHeight: hideLeftPanel ? 'auto' : 'calc(100vh - 4rem)' }}>
        {!hideLeftPanel && (
          <div className="relative w-full flex-col overflow-hidden lg:w-[45%] lg:flex">
            <OriginalLeftPanel />
          </div>
        )}

        {/* RIGHT PANEL — FORM */}
        <div className="relative flex flex-1 flex-col bg-[#f8faff] p-8 lg:p-10">
          <div className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(44,120,185,0.04) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="pointer-events-none absolute top-0 right-0 h-24 w-24 bg-[#2176B3] sm:h-32 sm:w-32 lg:h-40 lg:w-40" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)', opacity: 0.12 }} />

          <div className="relative z-10 mx-auto w-full max-w-xl">
            <motion.div custom={0.3} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: dur, delay: 0.3 }}>
              <h2 className="text-[22px] font-extrabold text-[#0f172a]">Send us a message</h2>
              <p className="mt-1 text-[13px] text-[#64748b]">
                Fill in the details below and our team will be in touch.
              </p>
            </motion.div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <motion.div custom={0.4} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.4 }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FloatingInput id="full_name" label="Full Name" icon={<User size={14} />} required value={formData.full_name} onChange={update('full_name')} />
                <FloatingInput id="email" label="Email Address" icon={<Mail size={14} />} type="email" required value={formData.email} onChange={update('email')} />
              </motion.div>

              <motion.div custom={0.45} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.45 }}>
                <PhoneInput id="phone" label="Phone Number" value={formData.phone} onChange={update('phone')} onCountryChange={(code) => setPhoneCountry(code.prefix)} onBlur={() => setErrors((prev) => ({ ...prev, phone: undefined }))} error={errors.phone} required />
              </motion.div>

              <motion.div custom={0.5} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.5 }}>
                <FloatingInput id="company" label="Company Name" icon={<Building2 size={14} />} value={formData.company} onChange={update('company')} />
              </motion.div>

              <motion.div custom={0.5} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.5 }}>
                <FloatingSelect id="subject" label="I'm interested in..." icon={<ChevronDown size={14} />} value={formData.subject} onChange={update('subject')} options={subjectOptions} />
              </motion.div>

              <motion.div custom={0.55} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.55 }}>
                <FloatingTextarea id="message" label="Your Message" icon={<MessageSquare size={14} />} value={formData.message} onChange={update('message')} rows={3} />
              </motion.div>

              <motion.div custom={0.6} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.6 }}>
                <ConsentSection required />
              </motion.div>

              <motion.div custom={0.65} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: dur, delay: 0.65 }}>
                <button type="submit" disabled={status === 'loading' || status === 'success' || status === 'error'}
                  className="mt-1 flex h-[52px] w-full items-center justify-center gap-2 rounded-xl font-bold text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-80"
                  style={{
                    background: status === 'success' ? 'linear-gradient(135deg, #059A8F, #047a71)' : 'linear-gradient(135deg, #2C78B9, #1a5fa0)',
                    boxShadow: status === 'success' ? '0 8px 24px rgba(5,154,143,0.35)' : '0 8px 24px rgba(44,120,185,0.3)',
                  }}
                  onMouseEnter={(e) => { if (status !== 'success') { e.currentTarget.style.background = 'linear-gradient(135deg, #1a5fa0, #2C78B9)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(44,120,185,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                  onMouseLeave={(e) => { if (status !== 'success') { e.currentTarget.style.background = 'linear-gradient(135deg, #2C78B9, #1a5fa0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(44,120,185,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  onMouseUp={(e) => { if (status !== 'success') e.currentTarget.style.transform = 'translateY(-1px)'; }}
                >
                  {status === 'loading' && (
                    <span className="inline-block h-4 w-4 rounded-full"
                      style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', animation: 'cmd-spin 0.6s linear infinite' }} />
                  )}
                  {status === 'success' && <CheckCircle size={16} />}
                  {status === 'idle' && <Send size={16} />}
                  <span className="text-[15px]">
                    {status === 'loading' ? 'Sending...' : status === 'success' ? 'Message Sent!' : 'Send Message'}
                  </span>
                </button>
              </motion.div>
            </form>


          </div>
        </div>
      </section>
    </>
  );
}
